// components/SendPackage/BusTransportSection.jsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Collapse,
  Chip,
  Stack,
  Button,
} from '@mui/material';
import { Bus, ChevronDown, ChevronUp, Clock, ArrowRight, Edit2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatEur, formatTime, formatDuration } from '../../../shared/components/Modals/BusTransportModal/transportUtils';

export const BusTransportSection = ({
  busTransportData,
  disableEdit,
  onEditBusOption,
  inModal = false,
}) => {

  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);



  const {
    name = '',
    start,
    end,
    price,
    legs = [],
  } = busTransportData;

  const departureTime = formatTime(start);
  const arrivalTime = formatTime(end);
  const duration = formatDuration(start, end);

  // Build summary chips
  const summaryItems = [];
  if (departureTime && arrivalTime) {
    summaryItems.push({ label: `${departureTime} – ${arrivalTime}` });
  }
  if (duration) {
    summaryItems.push({ icon: <Clock size={12} />, label: duration });
  }
  if (price != null) {
    summaryItems.push({ label: formatEur(price) });
  }


  const getStepIcon = (type) => {
    switch (type) {
      case 'bus-transport': return '🚌';
      case 'car': return '🚗';
      case 'wait': return '⏳';
      default: return '📦';
    }
  };

  const getStepLabel = (type) => {
    switch (type) {
      case 'bus-transport': return t('delivery.busStep') || 'Bus';
      case 'car': return t('delivery.carStep') || 'Car';
      case 'wait': return t('delivery.waitStep') || 'Wait';
      default: return type;
    }
  };

  return (
    <Box>
      {/* Clickable Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          py: 1,
          px: inModal ? 0 : 1,
          borderRadius: 1,
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Bus size={18} />
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              {t('delivery.busTransport') || 'Bus Transport'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {name || t('delivery.busRoute') || 'Bus route'}
            </Typography>
          </Box>
        </Box>
        {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </Box>

      {/* Summary chips when collapsed */}
      {!expanded && summaryItems.length > 0 && (
        <Box sx={{ pl: inModal ? 3.5 : 4.5, pt: 0.5, pb: 1 }}>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {summaryItems.map((item, idx) => (
              <Chip
                key={idx}
                icon={item.icon || undefined}
                label={item.label}
                size="small"
                variant="outlined"
                sx={{
                  height: 24,
                  fontSize: '0.75rem',
                  '& .MuiChip-icon': {
                    marginLeft: '6px',
                  },
                  '& .MuiChip-label': {
                    px: 1,
                  },
                }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Expandable Content */}
      <Collapse in={expanded}>
        <Box sx={{ pl: inModal ? 3.5 : 4.5, pt: 1.5 }}>
          {/* Route overview */}
          <Box
            sx={{
              p: 1.5,
              mb: 2,
              borderRadius: 1.5,
              bgcolor: 'primary.50',
              border: '1px solid',
              borderColor: 'primary.100',
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" alignItems="baseline" spacing={0.75}>
                <Typography variant="body1" fontWeight={700}>
                  {departureTime}
                </Typography>
                <Typography variant="body2" color="text.secondary">–</Typography>
                <Typography variant="body1" fontWeight={700}>
                  {arrivalTime}
                </Typography>
              </Stack>
              <Typography variant="body1" fontWeight={700}>
                {formatEur(price)}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
              <Typography variant="caption" color="text.secondary">
                {duration}
              </Typography>
              <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
              <Typography variant="caption" color="text.secondary">
                {legs.length} {t('delivery.segments') || 'segments'}
              </Typography>
            </Stack>
          </Box>

          {/* Steps timeline */}
          <Typography
            variant="caption"
            fontWeight={600}
            color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5, display: 'block' }}
          >
            {t('delivery.routeSteps') || 'Route Steps'}
          </Typography>

          <Stack spacing={0}>
            {legs.map((step, index) => {
              const stepTime = formatTime(step.start);
              const stepEndTime = formatTime(step.end);
              const isLast = index === legs.length - 1;

              return (
                <Box key={index} sx={{ display: 'flex', gap: 1.5 }}>
                  {/* Timeline line */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: 20,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: step.deliveryTransportType === 'bus-transport'
                          ? 'primary.main'
                          : step.deliveryTransportType === 'car'
                            ? 'warning.main'
                            : 'text.disabled',
                        mt: 0.75,
                      }}
                    />
                    {!isLast && (
                      <Box
                        sx={{
                          width: 2,
                          flex: 1,
                          bgcolor: 'divider',
                          my: 0.5,
                        }}
                      />
                    )}
                  </Box>

                  {/* Step content */}
                  <Box sx={{ pb: isLast ? 0 : 2, flex: 1, minWidth: 0 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Typography variant="body2" fontWeight={600}>
                            {getStepIcon(step.type)} {getStepLabel(step.type)}
                          </Typography>
                          {step.shortName && (
                            <Chip
                              label={step.shortName}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {step.origin}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <ArrowRight size={12} />
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {step.destination}
                          </Typography>
                        </Stack>
                        {step.exactName && (
                          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
                            {step.exactName}
                          </Typography>
                        )}
                      </Box>

                      <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 1 }}>
                        <Typography variant="caption" fontWeight={500}>
                          {stepTime} – {stepEndTime}
                        </Typography>
                        {/* {step.price != null && step.price > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {formatEur(step.price)}
                          </Typography>
                        )} */}
                      </Box>
                    </Stack>
                  </Box>
                </Box>
              );
            })}
          </Stack>

          {/* Edit button */}
          {!disableEdit && onEditBusOption && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Edit2 size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onEditBusOption();
                }}
                sx={{ borderRadius: 2 }}
              >
                {t('delivery.changeBusOption') || 'Change Bus Option'}
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};